/**
 * MilestonesView — displays all milestones grouped by category with progress tracking
 */

import { useState, useMemo } from 'react';
import { useGameStore } from '@/state';
import { MilestoneCategory, MilestoneDefinition, Quality, PlayerState } from '@/types';
import { MILESTONE_DEFINITIONS } from '@/data';
import { getZoneDefinition } from '@/data/zones/zoneData';
import { ScrollableView } from '@/components/layout/ScrollableView';
import { MilestoneCard } from './MilestoneCard';

/** Category display config */
const CATEGORY_CONFIG: Record<MilestoneCategory, { label: string; icon: string }> = {
  [MilestoneCategory.COLLECTION]: { label: 'Collection', icon: '📚' },
  [MilestoneCategory.ECONOMY]:    { label: 'Economy',    icon: '💰' },
  [MilestoneCategory.FISHING]:    { label: 'Fishing',    icon: '🎣' },
  [MilestoneCategory.BUDDY]:      { label: 'Buddies',    icon: '🤝' },
  [MilestoneCategory.STREAK]:     { label: 'Streaks',    icon: '🔥' },
  [MilestoneCategory.TRAPPING]:   { label: 'Trapping',   icon: '🪤' },
};

/** Category display order */
const CATEGORY_ORDER: MilestoneCategory[] = [
  MilestoneCategory.COLLECTION,
  MilestoneCategory.ECONOMY,
  MilestoneCategory.FISHING,
  MilestoneCategory.BUDDY,
  MilestoneCategory.STREAK,
  MilestoneCategory.TRAPPING,
];

/**
 * Compute current progress and target for a milestone condition.
 * Returns undefined if progress is not trackable (e.g., streak-based).
 */
function getMilestoneProgress(
  conditionKey: string,
  playerState: PlayerState,
): { current: number; target: number } | undefined {
  const stats = playerState.stats;

  switch (conditionKey) {
    // Collection
    case 'first_discovery':
      return { current: playerState.discoveredFish.length, target: 1 };
    case 'amateur_naturalist':
      return { current: playerState.discoveredFish.length, target: 5 };
    case 'master_naturalist': {
      try {
        const zone = getZoneDefinition(playerState.currentZone);
        const discoveredIds = new Set(playerState.discoveredFish.map((d) => d.definitionId));
        const found = zone.fishPool.filter((id) => discoveredIds.has(id)).length;
        return { current: found, target: zone.fishPool.length };
      } catch {
        return undefined;
      }
    }
    case 'quality_hunter': {
      const required = [Quality.COMMON, Quality.UNCOMMON, Quality.RARE, Quality.EPIC, Quality.LEGENDARY];
      const found = required.filter((q) => (stats.fishCaughtByQuality[q] ?? 0) > 0).length;
      return { current: found, target: required.length };
    }
    case 'perfect_specimen':
      return { current: Math.min(1, stats.fishCaughtByQuality[Quality.UNIQUE] ?? 0), target: 1 };

    // Economy
    case 'first_sale':
      return { current: Math.min(1, stats.totalFishSold), target: 1 };
    case 'merchant':
      return { current: Math.min(1000, stats.totalCurrencyEarned), target: 1000 };
    case 'tycoon':
      return { current: Math.min(10000, stats.totalCurrencyEarned), target: 10000 };
    case 'fish_baron':
      return { current: Math.min(100000, stats.totalCurrencyEarned), target: 100000 };

    // Fishing
    case 'novice':
      return { current: Math.min(10, stats.totalFishCaught), target: 10 };
    case 'experienced':
      return { current: Math.min(100, stats.totalFishCaught), target: 100 };
    case 'expert':
      return { current: Math.min(500, stats.totalFishCaught), target: 500 };
    case 'master_angler':
      return { current: Math.min(1000, stats.totalFishCaught), target: 1000 };

    // Buddy
    case 'first_hire': {
      const count = playerState.fishingBuddies.length + playerState.haulerBuddies.length;
      return { current: Math.min(1, count), target: 1 };
    }
    case 'full_crew':
      return { current: Math.min(3, playerState.fishingBuddies.length), target: 3 };
    case 'automation': {
      let totalTrips = 0;
      for (const hauler of playerState.haulerBuddies) {
        totalTrips += hauler.totalTripsCompleted ?? 0;
      }
      return { current: Math.min(10, totalTrips), target: 10 };
    }

    // Streak milestones use transient data — not trackable from PlayerState
    case 'hot_streak':
    case 'on_fire':
    case 'unstoppable':
      return undefined;

    default:
      return undefined;
  }
}

export function MilestonesView() {
  const earnedMilestones = useGameStore((s) => s.playerState.earnedMilestones);
  const playerState = useGameStore((s) => s.playerState);

  const earnedSet = useMemo(() => new Set(earnedMilestones ?? []), [earnedMilestones]);

  // Group milestones by category
  const grouped = useMemo(() => {
    const map = new Map<MilestoneCategory, MilestoneDefinition[]>();
    for (const cat of CATEGORY_ORDER) {
      map.set(cat, []);
    }
    for (const m of MILESTONE_DEFINITIONS) {
      const arr = map.get(m.category);
      if (arr) arr.push(m);
    }
    return map;
  }, []);

  const totalEarned = earnedSet.size;
  const totalMilestones = MILESTONE_DEFINITIONS.length;

  // Collapsible state per category
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleCategory = (cat: MilestoneCategory) => {
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  return (
    <ScrollableView gap={6}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Milestones</h1>
        <span className="text-sm text-gray-400">
          {totalEarned} / {totalMilestones}
        </span>
      </div>

      {/* Overall progress bar */}
      <div>
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-yellow-500 rounded-full transition-all"
            style={{ width: `${totalMilestones > 0 ? (totalEarned / totalMilestones) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Category sections */}
      {CATEGORY_ORDER.map((cat) => {
        const milestones = grouped.get(cat) ?? [];
        const config = CATEGORY_CONFIG[cat];
        const catEarned = milestones.filter((m) => earnedSet.has(m.id)).length;
        const isCollapsed = collapsed[cat] ?? false;

        return (
          <section key={cat}>
            <button
              onClick={() => toggleCategory(cat)}
              className="w-full flex items-center justify-between mb-2 hover:bg-gray-800/50 rounded-lg px-2 py-1.5 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{config.icon}</span>
                <h2 className="text-base font-semibold text-white">{config.label}</h2>
                <span className="text-xs text-gray-500">
                  ({catEarned}/{milestones.length})
                </span>
              </div>
              <span className={`text-gray-500 text-xs transition-transform ${isCollapsed ? '' : 'rotate-180'}`}>
                ▼
              </span>
            </button>

            {!isCollapsed && (
              <div className="space-y-2">
                {milestones.map((m) => {
                  const isEarned = earnedSet.has(m.id);
                  const prog = isEarned ? undefined : getMilestoneProgress(m.conditionKey, playerState);
                  return (
                    <MilestoneCard
                      key={m.id}
                      milestone={m}
                      earned={isEarned}
                      progress={prog?.current}
                      target={prog?.target}
                    />
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </ScrollableView>
  );
}
